import React, { useEffect, useRef, useState } from 'react';
import { bool, object, string } from 'prop-types';
import { Divider, Typography } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import hljs from 'highlight.js/lib/core';
import json from 'highlight.js/lib/languages/json';

import { CodeBlockContainer, CopyButton, Header } from './styles';

hljs.registerLanguage('json', json);

const CodeBlock = ({
  code, title, error, lang, styles,
}) => {
  const [showCopied, setShowCopied] = useState(false);
  const codeRef = useRef();

  const handleCopy = () => {
    setShowCopied(true);
    navigator.clipboard.writeText(code);
    setTimeout(() => {
      setShowCopied(false);
    }, 2000);
  };

  useEffect(() => {
    // when the code updates/component rerenders, we need to unset
    // the highlighted attribute on the code element
    // so that highlight.js can rehighlight the code
    const element = codeRef.current;
    if (element) {
      delete element.dataset.highlighted;
      hljs.highlightElement(element);
    }
  }, [code]);


  if (!code && !error) {
    return null;
  }

  if (error) {
    return (
      <CodeBlockContainer minHeight="200px">
        <Typography variant="caption" color="code.white">{error}</Typography>
      </CodeBlockContainer>
    );
  }

  return (
    <CodeBlockContainer sx={{ ...styles }}>
      {title && (
        <Header>
          <Typography>{title}</Typography>
          <Divider />
        </Header>
      )}
      <CopyButton size="small" aria-label="copy" onClick={handleCopy} sx={{ border: 'none' }}>
        {showCopied && (
          <span>Copied <CheckRoundedIcon fontSize="inherit" /></span>
        )}
        <ContentCopyRoundedIcon fontSize="inherit" />
      </CopyButton>
      <pre>
        <code role="code" className={`language-${lang}`} ref={codeRef}>{code}</code>
      </pre>
    </CodeBlockContainer>
  );
};

export default CodeBlock;

CodeBlock.propTypes = {
  code: string,
  title: string,
  error: bool,
  lang: string,
  styles: object,
};

CodeBlock.defaultProps = {
  code: null,
  title: '',
  error: false,
  lang: 'json',
  styles: {},
};
